// Generic, customizable logger utility for client-side use
// Usage: import { logger } from '@components/utility/logger'

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LoggerOptions {
  prefix?: string
  module?: string
  enabled?: boolean
  color?: string // CSS color string, e.g. 'red', '#ff0', 'rgb(0,0,0)'
}

function isDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false
  // Check for ?debug=true or ?debug=false in URL
  const params = new URLSearchParams(window.location.search)
  if (params.has('debug')) {
    return params.get('debug') === 'true'
  }
  // Enable for localhost or staging
  const host = window.location.hostname
  if (host.includes('localhost') || host.includes('staging')) return true
  // Future: check env var
  if ((window as unknown as { __FORCE_DEBUG_LOGS__?: boolean }).__FORCE_DEBUG_LOGS__) return true
  return false
}

export class Logger {

  private prefix: string
  private module?: string
  private enabled: boolean
  private color?: string

  constructor(options: LoggerOptions = {}) {
    this.prefix = options.prefix || ''
    this.module = options.module
    this.enabled = typeof options.enabled === 'boolean' ? options.enabled : isDebugEnabled()
    this.color = options.color
  }

  private format(...args: unknown[]): unknown[] {
    const parts: unknown[] = [this.prefix]
    if (this.module) parts.push(`[${this.module}]`)
    return [...parts, ...args]
  }

  private withColor(args: unknown[], colorOverride?: string): [string, string, ...unknown[]] | unknown[] {
    const color = colorOverride || this.color
    if (color) {
      const prefixParts = [this.prefix]
      if (this.module) prefixParts.push(`[${this.module}]`)
      const prefixStr = prefixParts.join(' ')
      return [`%c${prefixStr}`, `color: ${color}; font-weight: bold;`, ...args]
    }
    return this.format(...args)
  }

  debug(...args: unknown[]) {
    let color: string | undefined
    if (typeof args[args.length - 1] === 'string' && /^#|rgb|hsl|[a-zA-Z]+$/.test(args[args.length - 1] as string)) {
      color = args.pop() as string
    }
    if (this.enabled) console.debug(...this.withColor(args, color))
  }

  info(...args: unknown[]) {
    let color: string | undefined
    if (typeof args[args.length - 1] === 'string' && /^#|rgb|hsl|[a-zA-Z]+$/.test(args[args.length - 1] as string)) {
      color = args.pop() as string
    }
    if (this.enabled) console.info(...this.withColor(args, color))
  }

  warn(...args: unknown[]) {
    let color: string | undefined
    if (typeof args[args.length - 1] === 'string' && /^#|rgb|hsl|[a-zA-Z]+$/.test(args[args.length - 1] as string)) {
      color = args.pop() as string
    }
    if (this.enabled) console.warn(...this.withColor(args, color))
  }

  error(...args: unknown[]) {
    let color: string | undefined
    if (typeof args[args.length - 1] === 'string' && /^#|rgb|hsl|[a-zA-Z]+$/.test(args[args.length - 1] as string)) {
      color = args.pop() as string
    }
    if (this.enabled) console.error(...this.withColor(args, color))
  }

  /**
   * Log all first-level boolean properties of an object, one per line, falses (red) first, then trues (green).
   * Non-boolean properties are logged one per line without color.
   * If nonBoolPropName is provided, only that non-boolean property is logged (if present).
   * Each line includes the logger's prefix and module.
   * @param obj The object to log
   * @param label Optional label for the log
   * @param nonBoolPropName Optional property name to filter non-boolean property logging
   */
  logBooleans(obj: Record<string, unknown>, label?: string, nonBoolPropName?: string) {
    if (!this.enabled || typeof obj !== 'object' || obj === null) return
    const entries = Object.entries(obj)
    const bools = entries.filter((entry) => typeof entry[1] === 'boolean')
    const nonBools = entries.filter((entry) => typeof entry[1] !== 'boolean')
    // Separate falses and trues
    const falses = bools.filter(([, v]) => v === false)
    const trues = bools.filter(([, v]) => v === true)
    const prefixParts = [this.prefix]
    if (this.module) prefixParts.push(`[${this.module}]`)
    const prefixStr = prefixParts.join(' ')
    if (label) console.log(`${prefixStr} ${label}`)
    // console.log(`--- Boolean Properties of ${label} --- ${prefixStr}`)
    for (const [key, value] of [...falses, ...trues]) {
      const color = value ? 'green' : 'red'
      console.log(`%c${prefixStr} ${key}: ${value}`, `color: ${color}; font-weight: bold;`)
    }
    if (nonBools.length > 0) {
      if (nonBoolPropName) {
        const found = nonBools.find(([key]) => key === nonBoolPropName)
        if (found) {
          console.log(`--- Non-Boolean Property '${nonBoolPropName}' of ${label} --- ${prefixStr}`)
          // Log in yellow color
          console.log(`%c${prefixStr} ${found[0]}:`, 'color: #e6b800; font-weight: bold;', found[1])
        }
      } else {
        console.log(`--- Non-Boolean Properties of ${label} --- ${prefixStr}`)
        for (const [key, value] of nonBools) {
          console.log(`${prefixStr} ${key}:`, value)
        }
      }
    }
    // console.log(`--- End of Properties of ${label} --- ${prefixStr}`)
  }

}

// Default export for checkout logs
export const checkoutLogger = new Logger({ prefix: '[CHECKOUT_DEBUG]' })
// Usage: checkoutLogger.debug('message', data)
